import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useApp } from "@/components/AppShell";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import IssueCard from "@/components/IssueCard";
import { useRealtime } from "@/lib/realtime";
import { STATUSES, typeMeta, prioMeta, initials, PRIORITIES, ISSUE_TYPES } from "@/lib/meta";
import { useSettings } from "@/lib/settings";
import { Plus, Trash2, Settings2, KanbanSquare, AlertTriangle, Search, Filter, X, User as UserIcon, Layers, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export default function Boards({ project }) {
  const { openIssue, users } = useApp();
  const { user: currentUser } = useAuth();
  const [boards, setBoards] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [issues, setIssues] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [openConfig, setOpenConfig] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);

  // Per-user local filter state
  const [filters, setFilters] = useState({
    search: "",
    myIssuesOnly: false,
    assigneeId: "all",
    issueType: "all",
    priority: "all",
    epicId: "all",
    swimlane: "none",
    sprintScope: "active",
  });

  const getStorageKey = (boardId) => {
    const userId = currentUser?.id || "anonymous";
    return `digijira_filters_${userId}_${project.id}_${boardId || "default"}`;
  };

  const load = async () => {
    const [b, i, s] = await Promise.all([
      api.get("/boards", { params: { project_id: project.id } }),
      api.get("/issues", { params: { project_id: project.id } }),
      api.get("/sprints", { params: { project_id: project.id } }),
    ]);
    setBoards(b.data); setIssues(i.data); setSprints(s.data);
    if (!selectedId && b.data.length) setSelectedId(b.data[0].id);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [project.id]);

  useRealtime((ev) => {
    if (ev.project_id === project.id && ["issue.created","issue.updated","issue.deleted","sprint.updated","board.updated"].includes(ev.type)) {
      load();
    }
  }, [project.id]);

  const board = boards.find(b => b.id === selectedId);
  const activeSprint = sprints.find(s => s.state === "active");

  // Load saved local filters when selected board changes
  useEffect(() => {
    if (!selectedId) return;
    const key = getStorageKey(selectedId);
    try {
      const saved = localStorage.getItem(key);
      if (saved) {
        const parsed = JSON.parse(saved);
        setFilters(prev => ({
          ...prev,
          ...parsed,
          // Validate sprint scope if active sprint doesn't match
          sprintScope: parsed.sprintScope || (activeSprint ? "active" : "all"),
        }));
        return;
      }
    } catch {}

    // Default fallback
    setFilters({
      search: "",
      myIssuesOnly: false,
      assigneeId: "all",
      issueType: "all",
      priority: "all",
      epicId: "all",
      swimlane: board?.swimlane || "none",
      sprintScope: activeSprint ? "active" : "all",
    });
  }, [selectedId, currentUser?.id]);

  // Persist local filters on change
  const updateFilter = (key, value) => {
    setFilters(prev => {
      const next = { ...prev, [key]: value };
      if (selectedId) {
        try {
          localStorage.setItem(getStorageKey(selectedId), JSON.stringify(next));
        } catch {}
      }
      return next;
    });
  };

  const resetFilters = () => {
    const fresh = {
      search: "",
      myIssuesOnly: false,
      assigneeId: "all",
      issueType: "all",
      priority: "all",
      epicId: "all",
      swimlane: "none",
      sprintScope: activeSprint ? "active" : "all",
    };
    setFilters(fresh);
    if (selectedId) {
      try {
        localStorage.setItem(getStorageKey(selectedId), JSON.stringify(fresh));
      } catch {}
    }
  };

  const hasActiveFilters = Boolean(
    filters.search ||
    filters.myIssuesOnly ||
    filters.assigneeId !== "all" ||
    filters.issueType !== "all" ||
    filters.priority !== "all" ||
    filters.epicId !== "all" ||
    filters.swimlane !== "none" ||
    filters.sprintScope !== (activeSprint ? "active" : "all")
  );

  const epics = useMemo(() => issues.filter(i => i.issue_type === "epic"), [issues]);

  const filtered = useMemo(() => {
    return issues.filter(i => {
      if (i.issue_type === "epic") return false;
      
      // 1. Sprint scope filter
      if (filters.sprintScope === "active") {
        if (!activeSprint || i.sprint_id !== activeSprint.id) return false;
      } else if (filters.sprintScope === "backlog") {
        if (i.sprint_id) return false;
      } else if (filters.sprintScope !== "all") {
        if (i.sprint_id !== filters.sprintScope) return false;
      }

      // 2. Text Search (Key or Summary)
      if (filters.search?.trim()) {
        const q = filters.search.trim().toLowerCase();
        const keyMatch = i.key && i.key.toLowerCase().includes(q);
        const summaryMatch = i.summary && i.summary.toLowerCase().includes(q);
        if (!keyMatch && !summaryMatch) return false;
      }

      // 3. Only My Issues
      if (filters.myIssuesOnly && currentUser) {
        if (i.assignee_id !== currentUser.id && i.reporter_id !== currentUser.id) return false;
      }

      // 4. Assignee
      if (filters.assigneeId && filters.assigneeId !== "all") {
        if (filters.assigneeId === "unassigned") {
          if (i.assignee_id) return false;
        } else if (i.assignee_id !== filters.assigneeId) {
          return false;
        }
      }

      // 5. Issue Type
      if (filters.issueType && filters.issueType !== "all") {
        if (i.issue_type !== filters.issueType) return false;
      }

      // 6. Priority
      if (filters.priority && filters.priority !== "all") {
        if (i.priority !== filters.priority) return false;
      }

      // 7. Epic
      if (filters.epicId && filters.epicId !== "all") {
        if (filters.epicId === "none") {
          if (i.epic_id) return false;
        } else if (i.epic_id !== filters.epicId) {
          return false;
        }
      }

      return true;
    });
  }, [issues, filters, activeSprint, currentUser]);

  const swimGroups = useMemo(() => {
    const sw = filters.swimlane || "none";
    if (sw === "none") return [{ id: "_", label: null, list: filtered }];
    if (sw === "assignee") {
      const groups = {};
      filtered.forEach(i => {
        const key = i.assignee_id || "_unassigned";
        (groups[key] = groups[key] || []).push(i);
      });
      return Object.entries(groups).map(([k, list]) => {
        const u = users.find(x => x.id === k);
        return { id: k, label: u?.name || "Unassigned", list, avatar: u?.avatar_url };
      });
    }
    if (sw === "priority") {
      const order = ["highest","high","medium","low","lowest"];
      return order.map(p => ({ id: p, label: p.toUpperCase(), list: filtered.filter(i => i.priority === p) })).filter(g => g.list.length);
    }
    if (sw === "epic") {
      const groups = {};
      filtered.forEach(i => {
        const key = i.epic_id || "_no_epic";
        (groups[key] = groups[key] || []).push(i);
      });
      return Object.entries(groups).map(([k, list]) => {
        const ep = epics.find(x => x.id === k);
        return { id: k, label: ep ? `${ep.key} · ${ep.summary}` : "Without Epic", list };
      });
    }
    return [{ id: "_", label: null, list: filtered }];
  }, [filtered, filters.swimlane, users, epics]);

  const onDrop = async (statusId) => {
    if (!dragId) return;
    setOverCol(null); const id = dragId; setDragId(null);
    const iss = issues.find(x => x.id === id);
    if (!iss || iss.status === statusId) return;
    await api.patch(`/issues/${id}`, { status: statusId });
    load();
  };

  const delBoard = async () => {
    if (boards.length <= 1) { toast.error("Can't delete the last board"); return; }
    if (!window.confirm(`Delete board "${board.name}"?`)) return;
    await api.delete(`/boards/${board.id}`);
    setSelectedId(null); load();
  };

  if (!board) {
    return (
      <div className="p-6">
        <div className="text-sm text-muted-foreground">Loading boards…</div>
      </div>
    );
  }

  return (
    <div className="p-6 flex flex-col h-full gap-3">
      {/* Top Bar: Board Selector, Global Controls */}
      <div className="flex items-center gap-2 flex-wrap justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={selectedId} onValueChange={setSelectedId}>
            <SelectTrigger className="w-56 h-9 font-medium" data-testid="boards-selector"><SelectValue /></SelectTrigger>
            <SelectContent>{boards.map(b => <SelectItem key={b.id} value={b.id}>{b.name} · {b.board_type}</SelectItem>)}</SelectContent>
          </Select>
          <div className="text-xs px-2 py-1 bg-muted rounded font-mono text-muted-foreground">
            {filtered.length} of {issues.filter(i => i.issue_type !== "epic").length} issues
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <Button size="sm" variant="outline" onClick={()=>setOpenConfig(true)} data-testid="board-config-btn" className="gap-1.5">
            <Settings2 className="w-4 h-4" /> Board Settings
          </Button>
          <Button size="sm" onClick={()=>setOpenCreate(true)} data-testid="new-board-btn" className="gap-1.5">
            <Plus className="w-4 h-4" /> New board
          </Button>
          <Button size="icon" variant="ghost" onClick={delBoard} data-testid="del-board-btn" title="Delete Board">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Jira Quick Filter Bar (Personalized View Per User) */}
      <div className="flex items-center gap-2 flex-wrap p-2.5 rounded-lg border bg-card/60 backdrop-blur-sm text-sm">
        {/* Search */}
        <div className="relative w-48">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input
            value={filters.search}
            onChange={(e) => updateFilter("search", e.target.value)}
            placeholder="Search board..."
            className="h-8 pl-8 text-xs"
            data-testid="board-search-input"
          />
        </div>

        {/* Only My Issues Toggle */}
        <Button
          type="button"
          size="sm"
          variant={filters.myIssuesOnly ? "default" : "outline"}
          onClick={() => updateFilter("myIssuesOnly", !filters.myIssuesOnly)}
          className={cn("h-8 text-xs gap-1.5", filters.myIssuesOnly && "bg-primary text-primary-foreground")}
          data-testid="board-my-issues-btn"
        >
          <UserIcon className="w-3.5 h-3.5" /> Only My Issues
        </Button>

        {/* Sprint Scope */}
        <Select value={filters.sprintScope} onValueChange={(v) => updateFilter("sprintScope", v)}>
          <SelectTrigger className="h-8 w-44 text-xs" data-testid="board-sprint-scope">
            <SelectValue placeholder="Sprint" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active Sprint {activeSprint ? `(${activeSprint.name})` : "(None)"}</SelectItem>
            <SelectItem value="all">All Issues (Sprint + Backlog)</SelectItem>
            <SelectItem value="backlog">Backlog Only</SelectItem>
            {sprints.map(s => <SelectItem key={s.id} value={s.id}>{s.name} ({s.state})</SelectItem>)}
          </SelectContent>
        </Select>

        {/* Assignee Filter */}
        <Select value={filters.assigneeId} onValueChange={(v) => updateFilter("assigneeId", v)}>
          <SelectTrigger className="h-8 w-36 text-xs" data-testid="board-assignee-filter">
            <SelectValue placeholder="Assignee" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Assignees</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {users.map(u => (
              <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Issue Type Filter */}
        <Select value={filters.issueType} onValueChange={(v) => updateFilter("issueType", v)}>
          <SelectTrigger className="h-8 w-32 text-xs" data-testid="board-type-filter">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {ISSUE_TYPES.filter(t => t.id !== "epic").map(t => (
              <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Priority Filter */}
        <Select value={filters.priority} onValueChange={(v) => updateFilter("priority", v)}>
          <SelectTrigger className="h-8 w-32 text-xs" data-testid="board-priority-filter">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            {PRIORITIES.map(p => (
              <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Epic Filter */}
        {epics.length > 0 && (
          <Select value={filters.epicId} onValueChange={(v) => updateFilter("epicId", v)}>
            <SelectTrigger className="h-8 w-36 text-xs" data-testid="board-epic-filter">
              <SelectValue placeholder="Epic" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Epics</SelectItem>
              <SelectItem value="none">Without Epic</SelectItem>
              {epics.map(e => (
                <SelectItem key={e.id} value={e.id}>{e.key} · {e.summary}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Swimlane Selector */}
        <Select value={filters.swimlane} onValueChange={(v) => updateFilter("swimlane", v)}>
          <SelectTrigger className="h-8 w-36 text-xs" data-testid="board-swimlane-filter">
            <Layers className="w-3.5 h-3.5 mr-1 text-muted-foreground" />
            <SelectValue placeholder="Swimlane" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No Swimlanes</SelectItem>
            <SelectItem value="assignee">By Assignee</SelectItem>
            <SelectItem value="priority">By Priority</SelectItem>
            {epics.length > 0 && <SelectItem value="epic">By Epic</SelectItem>}
          </SelectContent>
        </Select>

        {/* Reset Filter Button */}
        {hasActiveFilters && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={resetFilters}
            className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground gap-1 ml-auto"
            data-testid="board-clear-filters-btn"
          >
            <RotateCcw className="w-3 h-3" /> Clear filters
          </Button>
        )}
      </div>

      {/* Board Columns & Cards */}
      <div className="flex-1 overflow-auto space-y-4">
        {swimGroups.map(group => (
          <div key={group.id}>
            {group.label && (
              <div className="flex items-center gap-2 mb-2 px-1">
                {group.avatar && <Avatar className="w-5 h-5"><AvatarImage src={group.avatar} /><AvatarFallback className="text-[8px]">{initials(group.label)}</AvatarFallback></Avatar>}
                <span className="text-xs uppercase tracking-widest text-muted-foreground font-medium">{group.label}</span>
                <span className="text-xs font-mono text-muted-foreground">{group.list.length}</span>
              </div>
            )}
            <div className="grid gap-3" style={{gridTemplateColumns: `repeat(${board.columns.length}, minmax(280px, 1fr))`}}>
              {board.columns.map(col => {
                const list = group.list.filter(i => i.status === col.status_id);
                const overWip = col.wip_limit && list.length > col.wip_limit;
                return (
                  <div key={col.status_id + group.id}
                    onDragOver={(e)=>{ e.preventDefault(); setOverCol(col.status_id + group.id); }}
                    onDragLeave={()=>setOverCol(null)}
                    onDrop={()=>onDrop(col.status_id)}
                    data-testid={`board-col-${col.status_id}`}
                    className={cn("rounded-md bg-secondary/40 border p-2 flex flex-col min-h-[240px]",
                      overCol === col.status_id + group.id ? "dashed-drop" : overWip ? "border-red-500/50" : "border-border/50")}>
                    <div className="flex items-center gap-2 px-2 py-1.5 text-xs uppercase tracking-widest font-medium">
                      <span>{col.name}</span>
                      <span className="text-muted-foreground font-mono">{list.length}{col.wip_limit ? ` / ${col.wip_limit}` : ""}</span>
                      {overWip && <AlertTriangle className="w-3.5 h-3.5 text-red-500" />}
                    </div>
                    <div className="space-y-2 flex-1">
                      {list.map(i => (
                        <IssueCard key={i.id} issue={i} users={users} onClick={()=>openIssue(i.id)}
                          draggable onDragStart={()=>setDragId(i.id)} onDragEnd={()=>setDragId(null)} dragging={dragId===i.id} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <NewBoardDialog open={openCreate} setOpen={setOpenCreate} project={project} onCreated={(b)=>{ setSelectedId(b.id); load(); }} />
      <ConfigBoardDialog open={openConfig} setOpen={setOpenConfig} board={board} onSaved={load} />
    </div>
  );
}

function NewBoardDialog({ open, setOpen, project, onCreated }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("kanban");
  const submit = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    const r = await api.post("/boards", { project_id: project.id, name, board_type: type });
    toast.success("Board created"); setOpen(false); setName(""); onCreated?.(r.data);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="new-board-dialog">
        <DialogHeader><DialogTitle className="font-display">New board</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input value={name} onChange={(e)=>setName(e.target.value)} placeholder="e.g. My team's board" data-testid="nb-name" /></div>
          <div><Label>Type</Label>
            <Select value={type} onValueChange={setType}><SelectTrigger data-testid="nb-type"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="kanban">Kanban</SelectItem><SelectItem value="scrum">Scrum</SelectItem></SelectContent></Select>
          </div>
        </div>
        <DialogFooter><Button onClick={submit} data-testid="nb-submit">Create</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConfigBoardDialog({ open, setOpen, board, onSaved }) {
  const { statuses } = useSettings() || {};
  const allStatuses = (statuses && statuses.length) ? statuses : STATUSES;
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    if (board) setDraft(JSON.parse(JSON.stringify(board)));
  }, [board?.id, open]);

  if (!draft) return null;

  const save = async () => {
    await api.patch(`/boards/${board.id}`, {
      name: draft.name,
      board_type: draft.board_type,
      columns: draft.columns,
    });
    toast.success("Board settings updated");
    setOpen(false);
    onSaved?.();
  };

  const addColumn = () => {
    const unused = allStatuses.find(s => !draft.columns.some(c => c.status_id === s.id)) || allStatuses[0];
    const newCol = {
      status_id: unused.id,
      name: unused.name || unused.label || unused.id,
      wip_limit: null,
    };
    setDraft({ ...draft, columns: [...draft.columns, newCol] });
  };

  const removeColumn = (index) => {
    if (draft.columns.length <= 1) {
      toast.error("Board must have at least one column");
      return;
    }
    setDraft({ ...draft, columns: draft.columns.filter((_, i) => i !== index) });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="config-board-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Board Settings</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div>
            <Label>Board Name</Label>
            <Input value={draft.name} onChange={(e)=>setDraft({...draft, name:e.target.value})} data-testid="cb-name" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Columns & Status Mapping</Label>
              <Button type="button" size="sm" variant="outline" onClick={addColumn} className="h-7 text-xs gap-1">
                <Plus className="w-3.5 h-3.5" /> Add column
              </Button>
            </div>
            <div className="space-y-2 max-h-[260px] overflow-y-auto p-2 border rounded-md bg-muted/20">
              {draft.columns.map((c, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <div className="w-36">
                    <Select
                      value={c.status_id}
                      onValueChange={(val) => {
                        const cs = [...draft.columns];
                        const matched = allStatuses.find(s => s.id === val);
                        const prevMatched = allStatuses.find(s => s.id === c.status_id);
                        const prevName = prevMatched?.name || prevMatched?.label || c.status_id;
                        cs[i] = {
                          ...c,
                          status_id: val,
                          name: (!c.name || c.name === prevName)
                            ? (matched?.name || matched?.label || val)
                            : c.name
                        };
                        setDraft({ ...draft, columns: cs });
                      }}
                    >
                      <SelectTrigger className="h-9"><SelectValue placeholder="Status" /></SelectTrigger>
                      <SelectContent>
                        {allStatuses.map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.name || s.label || s.id}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Input
                    value={c.name}
                    placeholder="Column Header"
                    onChange={(e) => {
                      const cs = [...draft.columns];
                      cs[i] = { ...c, name: e.target.value };
                      setDraft({ ...draft, columns: cs });
                    }}
                    className="flex-1 h-9"
                  />
                  <Input
                    type="number"
                    min="0"
                    value={c.wip_limit ?? ""}
                    placeholder="WIP"
                    className="w-20 h-9"
                    onChange={(e) => {
                      const cs = [...draft.columns];
                      cs[i] = { ...c, wip_limit: e.target.value ? Number(e.target.value) : null };
                      setDraft({ ...draft, columns: cs });
                    }}
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 text-muted-foreground hover:text-red-500 shrink-0"
                    onClick={() => removeColumn(i)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">
              Column status mapping and WIP limits apply to all team members sharing this board.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={()=>setOpen(false)}>Cancel</Button>
          <Button onClick={save} data-testid="cb-save">Save Settings</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
